import * as types from "../actions/actionTypes";

const sameId = (a, b) => String(a) === String(b);

// Applies an optimistic patch to a list without re-sorting or filtering what the backend returned.
export const applyListPatch = (list, action) => {
  const current = Array.isArray(list) ? list : [];
  switch (action.op) {
    case "prepend":
      return [action.item, ...current];
    case "merge":
      return current.map((row) => (sameId(row.id, action.id) ? { ...row, ...action.changes } : row));
    case "replace":
      return current.map((row) => (sameId(row.id, action.id) ? action.item : row));
    case "remove":
      return current.filter((row) => !sameId(row.id, action.id));
    case "insertAt": {
      const next = [...current];
      next.splice(Math.min(action.index ?? 0, next.length), 0, action.item);
      return next;
    }
    case "mergeAll":
      return current.map((row) => ({ ...row, ...action.changes }));
    default:
      return current;
  }
};

export default function createEntityReducer({ entity, listKey, itemKey, getAll, getAllResponse, getByIdResponse, extra = {} }) {
  const initialState = {
    [listKey]: [],
    [itemKey]: null,
    loading: false,
    loaded: false,
    query: {},
    ...Object.fromEntries(Object.entries(extra).map(([key, cfg]) => [key, cfg.initial])),
  };

  return (state = initialState, action) => {
    switch (action.type) {
      case getAll: {
        const { callback, silent, ...query } = action.payload || {};
        return { ...state, loading: !silent, query: silent ? state.query : query };
      }
      case getAllResponse:
        return { ...state, [listKey]: Array.isArray(action.payload) ? action.payload : [], loading: false, loaded: true };
      case getByIdResponse:
        return { ...state, [itemKey]: action.payload && !Array.isArray(action.payload) ? action.payload : null };
      case types.ENTITY_LIST_PATCH: {
        if (action.entity !== entity) return state;
        const next = { ...state, [listKey]: applyListPatch(state[listKey], action) };
        const item = state[itemKey];
        if (item && sameId(item.id, action.id)) {
          if (action.op === "merge") next[itemKey] = { ...item, ...action.changes };
          if (action.op === "replace") next[itemKey] = action.item;
          if (action.op === "remove") next[itemKey] = null;
        }
        return next;
      }
      default: {
        const handler = Object.entries(extra).find(([, cfg]) => cfg.type === action.type);
        if (!handler) return state;
        const [key, cfg] = handler;
        return { ...state, [key]: cfg.reduce ? cfg.reduce(action.payload, state[key]) : action.payload };
      }
    }
  };
}
